import * as React from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

export interface TagsInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  value?: string[];
  onChange: (value: string[]) => void;
  /** Keys that commit the current draft as a tag. Defaults to Enter and comma. */
  delimiters?: string[];
}

/**
 * A controlled tag/chip input in the shadcn/ui style. Type a term and press
 * Enter (or comma) to add it; Backspace on an empty field removes the last tag;
 * click a chip's X to remove it. Entries are trimmed, empties dropped, and
 * duplicates ignored.
 */
const TagsInput = React.forwardRef<HTMLInputElement, TagsInputProps>(
  ({ className, value = [], onChange, delimiters = ['Enter', ','], disabled, ...props }, ref) => {
    const [draft, setDraft] = React.useState('');

    const addTag = () => {
      const tag = draft.trim();
      if (!tag) return;
      if (!value.includes(tag)) onChange([...value, tag]);
      setDraft('');
    };

    const removeTag = (tag: string) => onChange(value.filter(t => t !== tag));

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (delimiters.includes(e.key)) {
        e.preventDefault();
        addTag();
      } else if (e.key === 'Backspace' && !draft && value.length) {
        onChange(value.slice(0, -1));
      }
    };

    return (
      <div
        className={cn(
          'flex min-h-10 w-full flex-wrap items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}>
        {value.map(tag => (
          <Badge key={tag} variant="secondary" className="gap-1">
            {tag}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => removeTag(tag)}>
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        <input
          ref={ref}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          value={draft}
          disabled={disabled}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addTag}
          {...props}
        />
      </div>
    );
  },
);
TagsInput.displayName = 'TagsInput';

export { TagsInput };
